import React, { memo } from 'react';
import { Box, Typography, Chip, Stack, alpha } from '@mui/material';
import { EventNote } from '@mui/icons-material';
import { DashboardEvent } from '../../types/dashboardTypes';
import {
    DashboardCard,
    DashboardEmptyState,
    DashboardListCardSkeleton,
    DashboardListRow,
    DateTile,
    thinScrollbarSx,
    useDashboardTones,
} from './DashboardCard';

interface UpcomingEventsCardProps {
    events: DashboardEvent[];
    loading?: boolean;
}

export const getDaysUntil = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    const diff = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return `In ${diff} days`;
};

/** Sizing shared by the three quick-info cards (exams, holidays, events). */
export const QUICK_INFO_CARD_SX = { height: 'auto', flex: 1, minHeight: 350 };

const UpcomingEventsCard: React.FC<UpcomingEventsCardProps> = memo(({ events, loading }) => {
    const { isDark, ink } = useDashboardTones();

    if (loading) {
        return <UpcomingEventsCardSkeleton />;
    }

    return (
        <DashboardCard accent="teal" icon={EventNote} title="Upcoming Events" sx={QUICK_INFO_CARD_SX}>
            {events.length === 0 ? (
                <DashboardEmptyState
                    accent="teal"
                    icon={EventNote}
                    title="No events scheduled"
                    description="No upcoming events in the next 30 days"
                    sx={{ p: 1.5, minHeight: 110 }}
                />
            ) : (
                <Box
                    sx={{
                        p: 1.5,
                        minHeight: 110,
                        maxHeight: 320,
                        overflowY: 'auto',
                        ...thinScrollbarSx(ink(isDark ? 0.15 : 0.12)),
                    }}
                >
                    <Stack spacing={1}>
                        {events.map((event) => (
                            <DashboardListRow key={event._id}>
                                <DateTile accent="teal" date={event.date} />

                                {/* Event info */}
                                <Box flex={1} minWidth={0}>
                                    <Typography variant="body2" fontWeight={500} noWrap fontSize={12}>
                                        {event.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" fontSize={10}>
                                        {getDaysUntil(event.date)}
                                    </Typography>
                                </Box>

                                {/* Category chip: the category's own colour */}
                                <Chip
                                    label={event.categoryName}
                                    size="small"
                                    sx={{
                                        bgcolor: event.categoryColor + '20',
                                        color: isDark
                                            ? alpha(event.categoryColor, 0.9)
                                            : event.categoryColor,
                                        fontSize: 8,
                                        height: 18,
                                        fontWeight: 500,
                                        flexShrink: 0
                                    }}
                                />
                            </DashboardListRow>
                        ))}
                    </Stack>
                </Box>
            )}
        </DashboardCard>
    );
});

UpcomingEventsCard.displayName = 'UpcomingEventsCard';

export const UpcomingEventsCardSkeleton: React.FC = memo(() => (
    <DashboardListCardSkeleton rowLead="tile" sx={{ flex: 1 }} />
));

UpcomingEventsCardSkeleton.displayName = 'UpcomingEventsCardSkeleton';

export default UpcomingEventsCard;
